import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Grid,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  IconButton,
  Tooltip,
  Avatar,
  Collapse,
  Checkbox
} from '@mui/material';
import {
  ExpandMore,
  ExpandLess,
  KeyboardArrowRight,
  KeyboardArrowUp,
  KeyboardArrowDown
} from '@mui/icons-material';
import Navbar from '../shared/Navbar';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { fetchDropdownData, fetchTimesheetAssignees, fetchTimesheetData, fetchUserTimesheetDetails, fetchTimesheetScrumSprintDropdowns, clearUserDetails, clearTimesheetData, exportTimesheetData } from '../../store/timesheetSlice';
import { DATE_RANGES, DATE_RANGE_LABELS, CUSTOM_SELECTED, TIMESHEET_STORAGE_KEYS, FILTER_VALUES, PROJECT_TYPES, SCRUM_BACKLOG_SENTINEL } from '../../shared/appConstants';
import { COLORS } from '../../shared/colors';
import { ICONS } from '../../shared/icons';
import { getPriorityColor } from '../dashboard/calendar-view/calendarColorUtils';
import { timesheetStyles } from '../../shared/globalSX';
import { ENDPOINTS } from '../../services/endpoints';
import translations from '../../shared/en.json';
import OverflowTooltip from '../../shared/OverflowTooltip';
import FilterPanel from '../dashboard/FilterPanel';
import PopperSelect from '../../shared/components/PopperSelect';
import ScrumSprintStateSelect from '../dashboard/ScrumSprintStateSelect';
import '../dashboard/FilterHeader.scss';
import './Timesheet.scss';

const TIMESHEET_DETAIL_GRID_COLUMNS = '45fr 45fr 40fr 80px';

const parseFilterValues = (saved) => {
  try {
    const parsed = saved ? JSON.parse(saved) : null;
    if (!parsed || typeof parsed !== 'object') {
      throw new Error();
    }
    return {
      selectedUsers: Array.isArray(parsed.selectedUsers) ? parsed.selectedUsers : [],
      selectedProjects: Array.isArray(parsed.selectedProjects) ? parsed.selectedProjects : [],
      selectedScrumSprints: Array.isArray(parsed.selectedScrumSprints) ? parsed.selectedScrumSprints : [],
      dateRange: typeof parsed.dateRange === 'string' ? parsed.dateRange : DATE_RANGES.SELECT_TIME,
      customDateFrom: typeof parsed.customDateFrom === 'string' ? parsed.customDateFrom : '',
      customDateTo: typeof parsed.customDateTo === 'string' ? parsed.customDateTo : ''
    };
  } catch {
    return {
      selectedUsers: [],
      selectedProjects: [],
      selectedScrumSprints: [],
      dateRange: DATE_RANGES.SELECT_TIME,
      customDateFrom: '',
      customDateTo: ''
    };
  }
};

const Timesheet = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { dropdownData, timesheetData, userDetails, userDetailsLoading, timesheetLoading } = useSelector((state) => state.timesheet);
  const selectedProjectFromTickets = useSelector((state) => state.tickets?.filters?.selectedProject);
  const prevTicketProjectRef = React.useRef(null);

  const [savedFilters] = useState(() => {
    try {
      const saved = localStorage.getItem(TIMESHEET_STORAGE_KEYS.FILTER_VALUES);
      return parseFilterValues(saved);
    } catch (e) {
      return parseFilterValues(null);
    }
  });

  const [selectedUsers, setSelectedUsers] = useState(() => savedFilters.selectedUsers);
  const [userFilterAnchor, setUserFilterAnchor] = useState(null);
  const [selectedProjects, setSelectedProjects] = useState(() => savedFilters.selectedProjects);
  const [projectFilterAnchor, setProjectFilterAnchor] = useState(null);
  const [selectedScrumSprints, setSelectedScrumSprints] = useState(() => savedFilters.selectedScrumSprints);
  const [scrumSprintDropdownReady, setScrumSprintDropdownReady] = useState(false);
  const [dateRange, setDateRange] = useState(() => savedFilters.dateRange);
  const [customDateFrom, setCustomDateFrom] = useState(() => savedFilters.customDateFrom);
  const [customDateTo, setCustomDateTo] = useState(() => savedFilters.customDateTo);
  const [tempDateFrom, setTempDateFrom] = useState('');
  const [tempDateTo, setTempDateTo] = useState('');
  const [isCustomRangeModalOpen, setIsCustomRangeModalOpen] = useState(false);

  useEffect(() => {
    if (!dropdownData.projects || dropdownData.projects.length === 0) return;
    const hasProjectContext = selectedProjectFromTickets
      && String(selectedProjectFromTickets).toLowerCase() !== String(FILTER_VALUES.ALL).toLowerCase();
    if (!hasProjectContext) return;
    // Only re-sync when the ticket project actually changes, so manual Timesheet
    // project selections made within the same session are not overridden.
    if (prevTicketProjectRef.current === String(selectedProjectFromTickets)) return;
    prevTicketProjectRef.current = String(selectedProjectFromTickets);
    const matchedProject = dropdownData.projects.find(
      (p) => String(p.id) === String(selectedProjectFromTickets)
    );
    if (matchedProject) {
      setSelectedProjects([matchedProject.name]);
    }
  }, [dropdownData.projects, selectedProjectFromTickets]);
  const [isSummaryOpen, setIsSummaryOpen] = useState(false);
  const [expandedUsers, setExpandedUsers] = useState({});
  const [expandedTickets, setExpandedTickets] = useState({});
  const [isExporting, setIsExporting] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      try {
        const filterValues = {
          selectedUsers,
          selectedProjects,
          selectedScrumSprints,
          dateRange,
          customDateFrom,
          customDateTo
        };
        localStorage.setItem(TIMESHEET_STORAGE_KEYS.FILTER_VALUES, JSON.stringify(filterValues));
      } catch (e) {
        console.warn('Storage quota exceeded:', e);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [
    selectedUsers,
    selectedProjects,
    selectedScrumSprints,
    dateRange,
    customDateFrom,
    customDateTo
  ]);

  const [visibleFilters, setVisibleFilters] = useState(['project', 'assignee', 'dateRange']);

  const projectByName = useMemo(() => (
    new Map((dropdownData.projects || []).map(project => [project.name, project]))
  ), [dropdownData.projects]);
  const selectedProjectObjects = useMemo(() => (
    selectedProjects.map(projectName => projectByName.get(projectName)).filter(Boolean)
  ), [projectByName, selectedProjects]);
  const selectedProjectIds = useMemo(() => selectedProjectObjects.map(project => project.id).filter(Boolean), [selectedProjectObjects]);
  const selectedProjectIdsKey = selectedProjectIds.join(',');
  const selectedUsersKey = selectedUsers.join(',');
  const selectedScrumSprintsKey = selectedScrumSprints.join(',');
  // Show Scrum sprint filtering only when every selected project is known and Scrum.
  // Mixed Scrum/Kanban selections cannot share a Scrum sprint filter safely.
  const shouldShowScrumSprintFilter = selectedProjectObjects.length > 0
    && selectedProjectObjects.length === selectedProjects.length
    && selectedProjectObjects.every(project => (project.project_type || '').toUpperCase() === PROJECT_TYPES.SCRUM.toUpperCase());

  useEffect(() => {
    dispatch(fetchDropdownData()).catch(error => {
      console.error('Failed to fetch dropdown data:', error);
    });
  }, [dispatch]);

  useEffect(() => {
    // Wait until projects are loaded so selectedProjectIdsKey is stable
    if (dropdownData.projects.length === 0) return;
    dispatch(fetchTimesheetAssignees(selectedProjectIds));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, selectedProjectIdsKey, dropdownData.projects.length]);

  useEffect(() => {
    if (!shouldShowScrumSprintFilter) {
      // Clear stale Scrum sprint state whenever the project selection becomes mixed,
      // empty, or unresolved so hidden filters never continue affecting the API call.
      setSelectedScrumSprints([]);
      setScrumSprintDropdownReady(false);
      setVisibleFilters(prev => prev.filter(id => id !== 'scrumSprint'));
      return;
    }

    let isActive = true;
    setScrumSprintDropdownReady(false);

    dispatch(fetchTimesheetScrumSprintDropdowns(selectedProjectIds))
      .unwrap()
      .catch(error => {
        console.error('Failed to fetch Scrum sprint dropdown data:', error);
      })
      .finally(() => {
        if (isActive) setScrumSprintDropdownReady(true);
      });

    return () => {
      isActive = false;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, selectedProjectIdsKey, shouldShowScrumSprintFilter]);

  useEffect(() => {
    if (!shouldShowScrumSprintFilter || !scrumSprintDropdownReady || selectedScrumSprints.length === 0) return;

    // Project selection can change after sprint choices are stored locally.
    // Drop any sprint IDs that are no longer present in the current dropdown set.
    const availableSprintIds = new Set((dropdownData.scrumSprints || []).map(sprint => sprint.id));
    const validSelectedScrumSprints = selectedScrumSprints.filter(id => id === SCRUM_BACKLOG_SENTINEL || availableSprintIds.has(id));
    if (validSelectedScrumSprints.length !== selectedScrumSprints.length) {
      setSelectedScrumSprints(validSelectedScrumSprints);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scrumSprintDropdownReady, selectedScrumSprintsKey, shouldShowScrumSprintFilter]);

  useEffect(() => {
    // Wait until projects are loaded so selectedProjectIdsKey is stable,
    // preventing an initial fire without projectIds followed by a second with them.
    if (dropdownData.projects.length === 0) return;

    // Clear user details cache and collapse rows when filters change
    dispatch(clearUserDetails());
    setExpandedUsers({});
    setExpandedTickets({});

    const params = {};

    if (dateRange && dateRange !== DATE_RANGES.SELECT_TIME) {
      const { startDate, endDate } = calculateDateRange(dateRange, customDateFrom, customDateTo);

      if (startDate && endDate) {
        params.fromDate = startDate;
        params.toDate = endDate;
      }
    }

    if (selectedUsers.length > 0) {
      params.userIds = selectedUsers.join(',');
    }

    if (selectedProjectIds.length > 0) {
      params.projectIds = selectedProjectIds.join(',');
    }

    // If the project is Scrum, wait until the sprint dropdown has finished loading
    // before firing — avoids duplicate fetches as scrumSprintDropdownReady flips.
    if (shouldShowScrumSprintFilter && !scrumSprintDropdownReady) return;

    if (shouldShowScrumSprintFilter && selectedScrumSprints.length > 0) {
      // Avoid dispatching a timesheet request with stale sprint IDs while the
      // validation effect above is about to normalize the saved filter state.
      const availableSprintIds = new Set((dropdownData.scrumSprints || []).map(sprint => sprint.id));
      const validSelectedScrumSprints = selectedScrumSprints.filter(id => id === SCRUM_BACKLOG_SENTINEL || availableSprintIds.has(id));
      if (validSelectedScrumSprints.length !== selectedScrumSprints.length) return;
      params.scrum_sprint_id = validSelectedScrumSprints.join(',');
    }

    if (dateRange === DATE_RANGES.SELECT_TIME) {
      // Clear data when Select Time is chosen
      dispatch(clearTimesheetData());
      return;
    }

    if (Object.keys(params).length > 0) {
      dispatch(fetchTimesheetData(params));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, dateRange, customDateFrom, customDateTo, selectedUsersKey, selectedProjectIdsKey, selectedScrumSprintsKey, scrumSprintDropdownReady, dropdownData.projects.length]);

  const userWorklogData = timesheetData.users || [];
  const dailySummary = timesheetData.daily_summary || [];
  const totalHours = timesheetData.total_working_hours || '0h 0m';
  const noDataMessage = dateRange === DATE_RANGES.SELECT_TIME
    ? translations.timesheet.selectDateRangeToViewWorkLogs
    : translations.timesheet.noWorkLogged;
  const hasDataToExport = userWorklogData.length > 0;
  const isExportDisabled = dateRange === DATE_RANGES.SELECT_TIME || isExporting || Boolean(timesheetLoading) || !hasDataToExport;

  const calculateDateRange = (dateRange, customDateFrom, customDateTo) => {
    const today = new Date();
    let startDate, endDate;

    switch (dateRange) {
      case DATE_RANGES.TODAY:
        startDate = endDate = today.toISOString().split('T')[0];
        break;
      case DATE_RANGES.YESTERDAY:
        const yesterday = new Date(today.getTime() - 24 * 60 * 60 * 1000);
        startDate = endDate = yesterday.toISOString().split('T')[0];
        break;
      case DATE_RANGES.THIS_WEEK:
        const dayOfWeek = today.getDay();
        const startOfWeek = new Date(today.getTime() - dayOfWeek * 24 * 60 * 60 * 1000);
        const endOfWeek = new Date(startOfWeek.getTime() + 6 * 24 * 60 * 60 * 1000);
        startDate = startOfWeek.toISOString().split('T')[0];
        endDate = endOfWeek.toISOString().split('T')[0];
        break;
      case DATE_RANGES.THIS_MONTH:
        const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
        const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);
        startDate = `${firstDay.getFullYear()}-${String(firstDay.getMonth() + 1).padStart(2, '0')}-${String(firstDay.getDate()).padStart(2, '0')}`;
        endDate = `${lastDay.getFullYear()}-${String(lastDay.getMonth() + 1).padStart(2, '0')}-${String(lastDay.getDate()).padStart(2, '0')}`;
        break;
      case DATE_RANGES.LAST_MONTH:
        startDate = new Date(today.getFullYear(), today.getMonth() - 1, 1).toISOString().split('T')[0];
        endDate = new Date(today.getFullYear(), today.getMonth(), 0).toISOString().split('T')[0];
        break;
      case DATE_RANGES.LAST_7_DAYS:
        startDate = new Date(today.getTime() - 6 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
        endDate = today.toISOString().split('T')[0];
        break;
      case DATE_RANGES.LAST_14_DAYS:
        startDate = new Date(today.getTime() - 13 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
        endDate = today.toISOString().split('T')[0];
        break;
      case DATE_RANGES.LAST_30_DAYS:
        startDate = new Date(today.getTime() - 29 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
        endDate = today.toISOString().split('T')[0];
        break;
      case DATE_RANGES.CUSTOM:
        if (customDateFrom && customDateTo) {
          startDate = customDateFrom;
          endDate = customDateTo;
        }
        break;
      default:
        break;
    }

    return { startDate, endDate };
  };

  const getSummaryTitle = () => {
    if (dateRange === DATE_RANGES.SELECT_TIME || dateRange === DATE_RANGES.CUSTOM) {
      return translations.timesheet.dailySummary;
    }
    const label = DATE_RANGE_LABELS[dateRange];
    return label ? `${label} ${translations.timesheet.summary}` : translations.timesheet.dailySummary;
  };

  const handleDateRangeChange = useCallback((value) => {
    if (value === DATE_RANGES.CUSTOM) {
      setTempDateFrom(customDateFrom);
      setTempDateTo(customDateTo);
      setIsCustomRangeModalOpen(true);
      return;
    }
    setDateRange(value);
  }, [customDateFrom, customDateTo]);

  const handleCustomRangeSubmit = () => {
    if (tempDateFrom && tempDateTo) {
      setCustomDateFrom(tempDateFrom);
      setCustomDateTo(tempDateTo);
      setDateRange(DATE_RANGES.CUSTOM);
    }
    setIsCustomRangeModalOpen(false);
  };


  const handleExportToExcel = async () => {
    if (isExportDisabled) return;
    setIsExporting(true);
    const params = {};

    if (dateRange && dateRange !== DATE_RANGES.SELECT_TIME) {
      const { startDate, endDate } = calculateDateRange(dateRange, customDateFrom, customDateTo);

      if (startDate && endDate) {
        params.fromDate = startDate;
        params.toDate = endDate;
      }
    }

    if (selectedUsers.length > 0) {
      params.userIds = selectedUsers.join(',');
    }

    if (selectedProjectIds.length > 0) {
      params.projectIds = selectedProjectIds.join(',');
    }

    if (shouldShowScrumSprintFilter && selectedScrumSprints.length > 0) {
      params.scrum_sprint_id = selectedScrumSprints.join(',');
    }

    try {
      const result = await dispatch(exportTimesheetData(params));
      if (result.payload) {
        const url = window.URL.createObjectURL(result.payload);
        const link = document.createElement('a');
        link.href = url;
        link.download = `timesheet-${new Date().toISOString().split('T')[0]}.xlsx`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        toast.success(t('timesheet.exportSuccess', 'Timesheet exported successfully.'));
      } else if (result.error) {
        toast.error(result.payload || result.error?.message || t('timesheet.exportFailed', 'Failed to export timesheet data.'));
      }
    } catch (error) {
      console.error('Export failed:', { error, params });
      toast.error(error?.message || t('timesheet.exportFailed', 'Failed to export timesheet data.'));
    } finally {
      setIsExporting(false);
    }
  };

  const handleClearFilters = () => {
    setSelectedProjects([]);
    setSelectedUsers([]);
    setSelectedScrumSprints([]);
    setDateRange(DATE_RANGES.SELECT_TIME);
    setCustomDateFrom('');
    setCustomDateTo('');
    setProjectFilterAnchor(null);
    setUserFilterAnchor(null);
  };

  const handleFilterClick = (filterId) => {
    setVisibleFilters(prev => (prev.includes(filterId) ? prev : [...prev, filterId]));
  };

  const handleRemoveFilter = (filterId) => {
    setVisibleFilters(prev => prev.filter(id => id !== filterId));
    if (filterId === 'project') setSelectedProjects([]);
    if (filterId === 'assignee') setSelectedUsers([]);
    if (filterId === 'scrumSprint') setSelectedScrumSprints([]);
    if (filterId === 'dateRange') {
      setDateRange(DATE_RANGES.SELECT_TIME);
      setCustomDateFrom('');
      setCustomDateTo('');
    }
    if (filterId === 'project') setProjectFilterAnchor(null);
    if (filterId === 'assignee') setUserFilterAnchor(null);
  };

  const hasClearableFilters = (
    selectedProjects.length > 0 ||
    selectedUsers.length > 0 ||
    selectedScrumSprints.length > 0 ||
    dateRange !== DATE_RANGES.SELECT_TIME
  );

  const resolvedAppliedCount = useMemo(() => {
    let count = 0;
    if (selectedProjects && selectedProjects.length > 0) count++;
    if (selectedUsers && selectedUsers.length > 0) count++;
    if (selectedScrumSprints && selectedScrumSprints.length > 0) count++;
    if (dateRange && dateRange !== DATE_RANGES.SELECT_TIME) count++;
    return count;
  }, [selectedProjects, selectedUsers, selectedScrumSprints, dateRange]);

  const timesheetFilters = useMemo(() => [
    {
      id: 'project',
      label: translations.timesheet.project,
      icon: ICONS.projects,
      active: visibleFilters.includes('project'),
      visible: visibleFilters.includes('project'),
      renderDropdown: () => (
        <PopperSelect
          disabled={dateRange === DATE_RANGES.SELECT_TIME}
          value={selectedProjects.length === 0 ? 'all' : selectedProjects.length === 1 ? selectedProjects[0] : 'multiple'}
          onChange={() => { }}
          open={Boolean(projectFilterAnchor)}
          onOpen={(e) => setProjectFilterAnchor(e.currentTarget)}
          onClose={(e) => {
            if (e?.target?.closest('.MuiMenuItem-root') ||
              e?.target?.closest('.MuiCheckbox-root') ||
              e?.target?.closest('.MuiFormControlLabel-root')) {
              return;
            }
            setProjectFilterAnchor(null);
          }}
          renderValue={(value) => {
            if (value === 'all') return translations.timesheet.allProjects;
            if (value === 'multiple') return `${selectedProjects.length} ${translations.timesheet.projects}`;
            return (
              <OverflowTooltip
                title={value}
                tooltipWidth={350}
                component={Box}
                className="timesheet-filter-value"
                placement="top-start"
              >
                {value}
              </OverflowTooltip>
            );
          }}
          MenuProps={{ PaperProps: { className: 'timesheet-dropdown-menu' } }}
        >
          <MenuItem
            value="all"
            className="timesheet-menu-item"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setSelectedProjects([]);
            }}
          >
            <Checkbox
              checked={selectedProjects.length === 0}
              size="small"
              className="timesheet-menu-checkbox"
            />
            {translations.timesheet.allProjects}
          </MenuItem>
          {(dropdownData.projects || []).map((project) => (
            <MenuItem
              key={project.id}
              value={project.name}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (selectedProjects.includes(project.name)) {
                  setSelectedProjects(prev => prev.filter(p => p !== project.name));
                } else {
                  setSelectedProjects(prev => [...prev, project.name]);
                }
              }}
              className="timesheet-menu-item"
            >
              <Checkbox
                checked={selectedProjects.includes(project.name)}
                size="small"
                className="timesheet-menu-checkbox"
              />
              <OverflowTooltip title={project.name} tooltipWidth={350} className="overflow-tooltip-dropdown" placement="top-start" />
            </MenuItem>
          ))}
        </PopperSelect>
      )
    },
    {
      id: 'assignee',
      label: translations.timesheet.assignee,
      icon: ICONS.assigneeBlue,
      active: visibleFilters.includes('assignee'),
      visible: visibleFilters.includes('assignee'),
      renderDropdown: () => (
        <PopperSelect
          disabled={dateRange === DATE_RANGES.SELECT_TIME}
          value={selectedUsers.length === 0 ? 'all' : selectedUsers.length === 1 ? selectedUsers[0] : 'multiple'}
          onChange={() => { }}
          open={Boolean(userFilterAnchor)}
          onOpen={(e) => setUserFilterAnchor(e.currentTarget)}
          onClose={(e) => {
            if (e?.target?.closest('.MuiMenuItem-root') ||
              e?.target?.closest('.MuiCheckbox-root') ||
              e?.target?.closest('.MuiFormControlLabel-root')) {
              return;
            }
            setUserFilterAnchor(null);
          }}
          renderValue={(value) => {
            if (value === 'all') return translations.timesheet.allUsers;
            if (value === 'multiple') return `${selectedUsers.length} ${translations.timesheet.users}`;
            const user = (dropdownData.assignees || []).find(u => u.id === value);
            const userName = user?.name || '';
            return (
              <OverflowTooltip
                title={userName}
                tooltipWidth={350}
                component={Box}
                className="timesheet-filter-value"
                placement="top-start"
              >
                {userName}
              </OverflowTooltip>
            );
          }}
          MenuProps={{ PaperProps: { className: 'timesheet-dropdown-menu' } }}
        >
          <MenuItem
            value="all"
            className="timesheet-menu-item"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setSelectedUsers([]);
            }}
          >
            <Checkbox
              checked={selectedUsers.length === 0}
              size="small"
              className="timesheet-menu-checkbox"
            />
            {translations.timesheet.allUsers}
          </MenuItem>
          {(dropdownData.assignees || []).map((user) => (
            <MenuItem
              key={user.id}
              value={user.id}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (selectedUsers.includes(user.id)) {
                  setSelectedUsers(prev => prev.filter(id => id !== user.id));
                } else {
                  setSelectedUsers(prev => [...prev, user.id]);
                }
              }}
              className="timesheet-menu-item"
            >
              <Checkbox
                checked={selectedUsers.includes(user.id)}
                size="small"
                className="timesheet-menu-checkbox"
              />
              <OverflowTooltip title={user.name} tooltipWidth={350} className="overflow-tooltip-dropdown" placement="top-start" />
            </MenuItem>
          ))}
        </PopperSelect>
      )
    },
    {
      id: 'dateRange',
      label: translations.timesheet.dateRange,
      icon: ICONS.dateRange,
      active: visibleFilters.includes('dateRange'),
      visible: visibleFilters.includes('dateRange'),
      renderDropdown: () => (
        <PopperSelect
          value={dateRange === DATE_RANGES.CUSTOM && customDateFrom && customDateTo ? CUSTOM_SELECTED : dateRange}
          onChange={(e) => handleDateRangeChange(e.target.value === CUSTOM_SELECTED ? DATE_RANGES.CUSTOM : e.target.value)}
          renderValue={(value) => {
            const label = value === CUSTOM_SELECTED
              ? `${customDateFrom} to ${customDateTo}`
              : (DATE_RANGE_LABELS[value] || value);
            return (
              <OverflowTooltip
                title={label}
                tooltipWidth={350}
                component={Box}
                className="timesheet-filter-value"
                placement="top-start"
              >
                {label}
              </OverflowTooltip>
            );
          }}
          MenuProps={{ PaperProps: { className: 'timesheet-dropdown-menu' } }}
        >
          <MenuItem value={DATE_RANGES.SELECT_TIME} className="timesheet-menu-item">{DATE_RANGE_LABELS[DATE_RANGES.SELECT_TIME]}</MenuItem>
          <MenuItem value={DATE_RANGES.TODAY} className="timesheet-menu-item">{DATE_RANGE_LABELS[DATE_RANGES.TODAY]}</MenuItem>
          <MenuItem value={DATE_RANGES.YESTERDAY} className="timesheet-menu-item">{DATE_RANGE_LABELS[DATE_RANGES.YESTERDAY]}</MenuItem>
          <MenuItem value={DATE_RANGES.THIS_WEEK} className="timesheet-menu-item">{DATE_RANGE_LABELS[DATE_RANGES.THIS_WEEK]}</MenuItem>
          <MenuItem value={DATE_RANGES.THIS_MONTH} className="timesheet-menu-item">{DATE_RANGE_LABELS[DATE_RANGES.THIS_MONTH]}</MenuItem>
          <MenuItem value={DATE_RANGES.LAST_MONTH} className="timesheet-menu-item">{DATE_RANGE_LABELS[DATE_RANGES.LAST_MONTH]}</MenuItem>
          <MenuItem value={DATE_RANGES.LAST_7_DAYS} className="timesheet-menu-item">{DATE_RANGE_LABELS[DATE_RANGES.LAST_7_DAYS]}</MenuItem>
          <MenuItem value={DATE_RANGES.LAST_14_DAYS} className="timesheet-menu-item">{DATE_RANGE_LABELS[DATE_RANGES.LAST_14_DAYS]}</MenuItem>
          <MenuItem value={DATE_RANGES.LAST_30_DAYS} className="timesheet-menu-item">{DATE_RANGE_LABELS[DATE_RANGES.LAST_30_DAYS]}</MenuItem>
          <MenuItem value={DATE_RANGES.CUSTOM} className="timesheet-menu-item">{DATE_RANGE_LABELS[DATE_RANGES.CUSTOM]}</MenuItem>
        </PopperSelect>
      )
    },
    ...(shouldShowScrumSprintFilter ? [{
      id: 'scrumSprint',
      label: 'Scrum Sprints',
      icon: ICONS.dropdownScrumSprint,
      active: visibleFilters.includes('scrumSprint'),
      visible: visibleFilters.includes('scrumSprint'),
      renderDropdown: () => (
        <ScrumSprintStateSelect
          disabled={dateRange === DATE_RANGES.SELECT_TIME}
          sprints={dropdownData.scrumSprints || []}
          value={selectedScrumSprints.length === 0 ? FILTER_VALUES.ALL : selectedScrumSprints.join(',')}
          onChange={(e) => {
            const raw = e.target.value;
            setSelectedScrumSprints(
              !raw || raw === FILTER_VALUES.ALL
                ? []
                : String(raw).split(',').filter(Boolean)
            );
          }}
          allLabel="All Scrum Sprints"
        />
      )
    }] : [])
  ], [
    selectedProjects,
    selectedUsers,
    dateRange,
    customDateFrom,
    customDateTo,
    projectFilterAnchor,
    userFilterAnchor,
    dropdownData.projects,
    dropdownData.assignees,
    dropdownData.scrumSprints,
    selectedScrumSprints,
    shouldShowScrumSprintFilter,
    visibleFilters,
    handleDateRangeChange
  ]);


  const toggleUserExpansion = (userId) => {
    const isExpanding = !expandedUsers[userId];
    setExpandedUsers(prev => ({
      ...prev,
      [userId]: isExpanding
    }));

    if (isExpanding && !userDetails[userId]) {
      const params = {};

      if (dateRange && dateRange !== DATE_RANGES.SELECT_TIME) {
        const { startDate, endDate } = calculateDateRange(dateRange, customDateFrom, customDateTo);

        if (startDate && endDate) {
          params.fromDate = startDate;
          params.toDate = endDate;
        }
      }

      if (selectedProjectIds.length > 0) {
        params.projectIds = selectedProjectIds.join(',');
      }

      if (shouldShowScrumSprintFilter && selectedScrumSprints.length > 0) {
        params.scrumSprintIds = selectedScrumSprints.join(',');
      }

      dispatch(fetchUserTimesheetDetails({
        userId,
        fromDate: params.fromDate,
        toDate: params.toDate,
        projectIds: params.projectIds,
        scrumSprintIds: params.scrumSprintIds
      }));
    }
  };

  const toggleTicketExpansion = (userId, ticketKey) => {
    const expansionKey = `${userId}:${ticketKey}`;
    setExpandedTickets(prev => ({
      ...prev,
      [expansionKey]: !prev[expansionKey]
    }));
  };


  return (
    <div className="dashboard">
      <Navbar />

      <main className="main timesheet-main-layout" >
        <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ px: 2, py: 1, borderRadius: '8px' }}>
          <Typography variant="h6" fontWeight={600} color={COLORS.text.muted}>
            {translations.timesheet.title}
          </Typography>
        </Box>

        <Box className="filter-header-container timesheet__filter-toolbar">
          <Box className="filter-header-content timesheet__filter-toolbar-content">
          <FilterPanel
            filters={timesheetFilters}
            onFilterClick={handleFilterClick}
            onRemoveFilter={handleRemoveFilter}
            onClearAll={handleClearFilters}
            hasClearable={hasClearableFilters}
            active={hasClearableFilters}
            appliedCount={resolvedAppliedCount}
          />
          <Tooltip title={translations.timesheet.exportToExcel} slotProps={{ tooltip: { className: 'app-tooltip' } }}>
            <span>
              <IconButton
                onClick={handleExportToExcel}
                disabled={isExportDisabled}
                className={`timesheet__export-button ${isExportDisabled ? 'timesheet__export-button--disabled' : ''}`}
              >
                <img src={ICONS.download} alt="" style={{ width: 18, height: 18 }} />
              </IconButton>
            </span>
          </Tooltip>
          </Box>
        </Box>

        {/* Integrated Table and Summary Layout */}
        <Box className="timesheet__content-wrapper">
          <TableContainer
            component={Paper}
            elevation={0}
            className="timesheet__table-container"
          >
          <Table sx={timesheetStyles.mainTable}>
            <colgroup>
              <col style={{ width: '45%' }} />
              <col style={{ width: '45%' }} />
              <col style={{ width: '40%' }} />
              <col style={{ width: '80px' }} />
            </colgroup>
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.50' }}>
                <TableCell align="left" className="timesheet__user-column-header" sx={{ pl: 8 }}><strong style={{ color: COLORS.brand.primaryDark }}>{translations.timesheet.user}</strong></TableCell>
                <TableCell align="center"><strong style={{ color: COLORS.brand.primaryDark }}>{translations.timesheet.tasksWorked}</strong></TableCell>
                <TableCell align="center"><strong style={{ color: COLORS.brand.primaryDark }}>{translations.timesheet.totalTime}</strong></TableCell>
                <TableCell align="right" sx={{ pr: 4 }}><strong></strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {userWorklogData.length > 0 ? (
                userWorklogData.map((userData) => (
                  <React.Fragment key={userData.id}>
                    {/* User Summary Row */}
                    <TableRow hover sx={timesheetStyles.userRow} onClick={() => toggleUserExpansion(userData.id)}>
                      <TableCell align="left" className="timesheet__user-column-body" sx={{ pl: 4 }}>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Avatar sx={{ width: 32, height: 32 }}>
                            {userData.name?.charAt(0) || 'U'}
                          </Avatar>
                          <Typography variant="body1" fontWeight="500">
                            {userData.name || translations.timesheet.unknownUser}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2" sx={{ color: COLORS.brand.primaryDark, fontWeight: 400 }}>
                          {userData.total_tasks} {userData.total_tasks !== 1 ? translations.timesheet.tasks : translations.timesheet.task}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2" sx={{ color: COLORS.brand.primaryDark, fontWeight: 400 }}>
                          {userData.total_time}
                        </Typography>
                      </TableCell>
                      <TableCell align="right" sx={{ pr: 4 }}>
                        <IconButton size="small">
                          {expandedUsers[userData.id] ? <ExpandLess /> : <ExpandMore />}
                        </IconButton>
                      </TableCell>

                    </TableRow>

                    {/* Expanded Worklog Details */}
                    <TableRow>
                      <TableCell colSpan={4} sx={timesheetStyles.expandedCell}>
                        <Collapse in={expandedUsers[userData.id]} timeout="auto" unmountOnExit>
                          <Box sx={timesheetStyles.expandedBox}>
                            <Box sx={{ px: 1, py: 1 }}>
                              {/* Nested Header */}
                              <Box display="grid" sx={{ gridTemplateColumns: TIMESHEET_DETAIL_GRID_COLUMNS, pb: 1, borderBottom: '1px solid', borderColor: 'grey.200', mb: 1.5, px: 2 }}>
                                <Typography variant="caption" sx={{ textAlign: 'left', fontWeight: 700, color: COLORS.gray[800] }}>
                                  {translations.timesheet.ticket}
                                </Typography>
                                <Typography variant="caption" sx={{ textAlign: 'center', fontWeight: 700, color: COLORS.gray[800] }}>
                                  {translations.timesheet.totalTime}
                                </Typography>
                                <Typography variant="caption" sx={{ textAlign: 'center', fontWeight: 700, color: COLORS.gray[800] }}>
                                  {translations.timesheet.entries}
                                </Typography>
                                <Box />
                              </Box>

                              {/* Nested Content */}
                              {userDetailsLoading[userData.id] ? (
                                <Box sx={{ py: 3, textAlign: 'center' }}>
                                  <Typography variant="body2" color="text.secondary">
                                    {translations.timesheet.loadingDetails}
                                  </Typography>
                                </Box>
                              ) : (userDetails[userData.id] || []).length > 0 ? (
                                userDetails[userData.id].map((ticketGroup, index) => {
                                  const colors = COLORS.timesheet.fallbackColors;
                                  const pName = ticketGroup.priority || '';
                                  const dynamicColor = getPriorityColor(pName);
                                  const rowColor = dynamicColor ? `${dynamicColor}26` : colors[index % colors.length];
                                  const isDeleted = ticketGroup.ticket_status === 'SOFT_DELETE';
                                  const ticketKey = ticketGroup.ticketId || ticketGroup.ticketNumber;
                                  const entries = Array.isArray(ticketGroup.entries) ? ticketGroup.entries : [];
                                  const expansionKey = `${userData.id}:${ticketKey}`;
                                  const isTicketExpanded = Boolean(expandedTickets[expansionKey]);

                                  return (
                                    <Box key={ticketKey} className="timesheet__ticket-group" sx={{ mb: 1.5 }}>
                                      <Box
                                        className={`timesheet__worklog-row timesheet__ticket-summary-row ${isDeleted ? 'timesheet__worklog-row--deleted' : ''}`}
                                        display="grid"
                                        alignItems="center"
                                        onClick={() => toggleTicketExpansion(userData.id, ticketKey)}
                                        role="button"
                                        aria-expanded={isTicketExpanded}
                                        tabIndex={0}
                                        onKeyDown={(event) => {
                                          if (event.key === 'Enter' || event.key === ' ') {
                                            event.preventDefault();
                                            toggleTicketExpansion(userData.id, ticketKey);
                                          }
                                        }}
                                        sx={{
                                          bgcolor: rowColor,
                                          gridTemplateColumns: TIMESHEET_DETAIL_GRID_COLUMNS,
                                          borderRadius: '8px',
                                          p: 1.5,
                                          px: 2,
                                          cursor: 'pointer',
                                          transition: 'transform 0.2s',
                                          '&:hover': { transform: 'scale(1.005)', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }
                                        }}
                                      >
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                                          <IconButton
                                            size="small"
                                            aria-label={`${isTicketExpanded ? 'Collapse' : 'Expand'} ${ticketGroup.ticketNumber}`}
                                            tabIndex={-1}
                                            sx={{ p: 0.25 }}
                                          >
                                            {isTicketExpanded ? <KeyboardArrowDown /> : <KeyboardArrowRight />}
                                          </IconButton>
                                          <img src={ICONS.tickets} alt="" className={`timesheet__ticket-icon ${isDeleted ? 'timesheet__ticket-icon--deleted' : ''}`} style={{ width: 18, height: 18, opacity: 0.8 }} />
                                          <Box>
                                            <Typography
                                              variant="body2"
                                              fontWeight="500"
                                              className={`timesheet__ticket-title ${isDeleted ? 'timesheet__ticket-title--deleted' : ''}`}
                                              sx={{ color: COLORS.brand.primaryDark, opacity: 0.9 }}
                                            >
                                              <Box
                                                component="span"
                                                className={`timesheet__ticket-number ${isDeleted ? 'timesheet__ticket-number--deleted' : ''}`}
                                                sx={{ 
                                                  fontWeight: 400,
                                                  cursor: 'pointer',
                                                  '&:hover': { textDecoration: 'underline' }
                                                }}
                                                onClick={isDeleted ? undefined : (e) => {
                                                  e.stopPropagation();
                                                  window.open(`/${ENDPOINTS.TASK_URL(ticketGroup.ticketNumber)}`, '_blank');
                                                }}
                                              >
                                                {ticketGroup.ticketNumber}
                                              </Box>
                                              <Box component="span" className={isDeleted ? 'timesheet__ticket-name--deleted' : ''}>
                                                {' '}{ticketGroup.title}
                                              </Box>
                                              {isDeleted && (
                                                <Box
                                                  component="span"
                                                  className="timesheet__deleted-label"
                                                >
                                                  {translations.timesheet.deleted || 'Deleted'}
                                                </Box>
                                              )}
                                            </Typography>
                                          </Box>
                                        </Box>
 
                                        <Typography variant="body2" className={`timesheet__log-time ${isDeleted ? 'timesheet__log-time--deleted' : ''}`} sx={{ textAlign: 'center', color: COLORS.brand.primaryDark, fontWeight: 400 }}>
                                          {ticketGroup.totalTime || ticketGroup.totalMinutes || '0h 0m'}
                                        </Typography>
                                        <Typography variant="body2" sx={{ textAlign: 'center', color: COLORS.brand.primaryDark, fontWeight: 400 }}>
                                          {ticketGroup.entryCount ?? entries.length}
                                        </Typography>
                                        <Box />
                                      </Box>

                                      <Collapse in={isTicketExpanded} timeout="auto" unmountOnExit>
                                        <Box className="timesheet__ticket-entries">
                                          <Box className="timesheet__worklog-entry-header" display="grid" sx={{ gridTemplateColumns: TIMESHEET_DETAIL_GRID_COLUMNS, px: 2, py: 1 }}>
                                            <Typography variant="caption" sx={{ textAlign: 'left', fontWeight: 700, color: COLORS.gray[800] }}>
                                              {translations.timesheet.date}
                                            </Typography>
                                            <Typography variant="caption" sx={{ textAlign: 'center', fontWeight: 700, color: COLORS.gray[800] }}>
                                              {translations.timesheet.timeLogged}
                                            </Typography>
                                            <Box />
                                            <Box />
                                          </Box>
                                          {entries.map((worklog, entryIndex) => (
                                            <Box
                                              key={worklog.worklogId || `${ticketKey}-${worklog.logDate}-${entryIndex}`}
                                              className={`timesheet__worklog-entry ${isDeleted ? 'timesheet__worklog-row--deleted' : ''}`}
                                              display="grid"
                                              alignItems="center"
                                              sx={{ gridTemplateColumns: TIMESHEET_DETAIL_GRID_COLUMNS, px: 2, py: 1 }}
                                            >
                                              <Typography variant="body2" className={`timesheet__log-date ${isDeleted ? 'timesheet__log-date--deleted' : ''}`} sx={{ textAlign: 'left', color: COLORS.brand.primaryDark, opacity: 0.8, fontWeight: 500 }}>
                                                {worklog.logDate}
                                              </Typography>
                                              <Typography variant="body2" className={`timesheet__log-time ${isDeleted ? 'timesheet__log-time--deleted' : ''}`} sx={{ textAlign: 'center', color: COLORS.brand.primaryDark, fontWeight: 400 }}>
                                                {worklog.totalMinutes}
                                              </Typography>
                                              <Box />
                                              <Box />
                                            </Box>
                                          ))}
                                        </Box>
                                      </Collapse>
                                    </Box>
                                  );
                                })
                              ) : (
                                <Box sx={{ py: 3, textAlign: 'center' }}>
                                  <Typography variant="body2" color="text.secondary">
                                    {translations.timesheet.noWorkLogged}
                                  </Typography>
                                </Box>
                              )}
                            </Box>
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={timesheetStyles.noDataCell}>
                    <Typography variant="body2" color="text.secondary">
                      {noDataMessage}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Daily Summary */}
        {/* Daily Summary */}
        {dailySummary.length > 0 && (
          <Box sx={{ ...timesheetStyles.summaryTab, bgcolor: COLORS.timesheet.summaryBg }} className="timesheet__summary-sticky">
            {/* Summary Tab Header - Anchored at the top while expanded */}
            <Box
              onClick={() => setIsSummaryOpen(!isSummaryOpen)}
              sx={{
                ...timesheetStyles.summaryHeader,
                bgcolor: COLORS.timesheet.summaryBg,
                color: COLORS.brand.primaryDark,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                px: 3,
                py: 1.5,
                borderTop: 'none',
                borderBottom: isSummaryOpen ? `1px solid ${COLORS.ui.greyE0E0E0}` : 'none',
                '&:hover': {
                  bgcolor: COLORS.timesheet.summaryHover
                }
              }}
            >
              {isSummaryOpen ? (
                <KeyboardArrowDown sx={{ fontSize: 32, color: COLORS.brand.primaryDark }} />
              ) : (
                <KeyboardArrowUp sx={{ fontSize: 32, color: COLORS.brand.primaryDark }} />
              )}
              
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <img src={ICONS.dateRange} alt="Date Range" style={{ width: 22, height: 22 }} />
                <Typography variant="subtitle1" fontWeight="600" sx={{ color: COLORS.brand.primaryDark }}>
                  {getSummaryTitle()} - {totalHours}
                </Typography>
              </Box>
            </Box>

            {/* Collapsible Summary Content */}
            <Collapse in={isSummaryOpen}>
              <Box className="timesheet__summary-content" sx={{ p: 2, pl: 4, bgcolor: COLORS.timesheet.summaryBg, overflowY: 'auto' }}>
                <Box display="flex" flexWrap="wrap" gap={3} sx={{ pl: 4 }}>
                  {dailySummary.map((day) => (
                    <Box
                      key={day.date}
                      className="timesheet__summary-card"
                      sx={{
                        background: COLORS.white,
                        border: `1px solid ${COLORS.brand.primaryDark}`,
                        borderRadius: 2,
                        textAlign: 'center'
                      }}
                    >
                      <Typography variant="body2" sx={{ mb: 0.5, fontSize: '0.8rem', color: COLORS.timesheet.dateText, fontWeight: 500 }}>
                        {day.date}
                      </Typography>
                      <Typography 
                        variant="h6" 
                        fontWeight="600" 
                        sx={{ 
                          fontSize: '1rem', 
                          color: COLORS.brand.primaryDark,
                          opacity: day.working_hours === '0h 0m' ? 0.4 : 1
                        }}
                      >
                        {day.working_hours}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            </Collapse>
          </Box>
        )}
      </Box>
    </main>



      <Dialog open={isCustomRangeModalOpen} onClose={() => setIsCustomRangeModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{translations.timesheet.selectCustomDateRange}</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  type="date"
                  label={translations.timesheet.fromDate}
                  value={tempDateFrom}
                  onChange={(e) => setTempDateFrom(e.target.value)}
                  onKeyDown={(e) => e.preventDefault()}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  type="date"
                  label={translations.timesheet.toDate}
                  value={tempDateTo}
                  onChange={(e) => setTempDateTo(e.target.value)}
                  onKeyDown={(e) => e.preventDefault()}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setIsCustomRangeModalOpen(false);
            setTempDateFrom('');
            setTempDateTo('');
          }}>{translations.timesheet.cancel}</Button>
          <Button onClick={handleCustomRangeSubmit} variant="contained">{translations.timesheet.apply}</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default Timesheet;
